import { z } from 'zod';

import { ApiProblemError } from '../../../app/api/slider-fetch';

export const CREATE_MODES = ['quick', 'guided'] as const;
export const GUIDED_STEPS = ['brief', 'style', 'review'] as const;
export type CreateMode = (typeof CREATE_MODES)[number];
export type GuidedStep = (typeof GUIDED_STEPS)[number];

/** URL state for `.../presentations/new`. Brief text never goes into the URL. */
export const NewPresentationSearchSchema = z.object({
  mode: z.enum(CREATE_MODES).optional().catch(undefined),
  step: z.enum(GUIDED_STEPS).optional().catch(undefined),
  style: z.string().trim().max(200).optional().catch(undefined),
});
export type NewPresentationSearch = z.infer<typeof NewPresentationSearchSchema>;

export const NAME_MAX = 160;
export const DESCRIPTION_MAX = 1000;
/** Matches the Style Handoff contract so the brief can prefill it. */
export const TOPIC_MAX = 240;
export const SLIDE_COUNT_MIN = 1;
export const SLIDE_COUNT_MAX = 100;

export interface BriefValues {
  name: string;
  /** Guidance only: not an API field. Feeds the ChatGPT handoff. */
  topic: string;
  description: string;
  /** Guidance only: kept as typed so partial input is never rewritten. */
  slideCount: string;
}

export const EMPTY_BRIEF: BriefValues = { description: '', name: '', slideCount: '', topic: '' };

export type BriefField = keyof BriefValues;
export type BriefIssue = 'required' | 'tooLong' | 'notWhole' | 'outOfRange' | 'rejected';
export type BriefErrors = Partial<Record<BriefField, BriefIssue>>;

/** Persian and Arabic-Indic digits are what a Persian keyboard types; read them as numbers. */
export function normalizeDigits(value: string): string {
  return value
    .replace(/[۰-۹]/gu, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/gu, (digit) => String(digit.charCodeAt(0) - 0x0660));
}

/** Pure validation shared by the step gate and the final Create guard. */
export function validateBrief(values: BriefValues, only?: readonly BriefField[]): BriefErrors {
  const all = validateAll(values);
  if (!only) return all;
  return Object.fromEntries(
    Object.entries(all).filter(([field]) => only.includes(field as BriefField)),
  );
}

function validateAll(values: BriefValues): BriefErrors {
  const errors: BriefErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = 'required';
  else if (name.length > NAME_MAX) errors.name = 'tooLong';
  if (values.topic.trim().length > TOPIC_MAX) errors.topic = 'tooLong';
  if (values.description.trim().length > DESCRIPTION_MAX) errors.description = 'tooLong';
  const count = normalizeDigits(values.slideCount.trim());
  if (count) {
    const parsed = Number(count);
    if (!/^\d+$/.test(count) || !Number.isSafeInteger(parsed)) errors.slideCount = 'notWhole';
    else if (parsed < SLIDE_COUNT_MIN || parsed > SLIDE_COUNT_MAX) errors.slideCount = 'outOfRange';
  }
  return errors;
}

/** Quick create has only these two fields; guidance fields must not block it. */
export const QUICK_FIELDS: readonly BriefField[] = ['name', 'description'];

export function slideCountOf(values: BriefValues): number | undefined {
  const count = normalizeDigits(values.slideCount.trim());
  return count && !validateBrief(values).slideCount ? Number(count) : undefined;
}

export type CreateFailure = 'validation' | 'denied' | 'missingProject' | 'session' | 'retryable';

/** Branches on HTTP status from the Problem Details envelope; never on message text. */
export function classifyCreateFailure(error: unknown): CreateFailure {
  if (error instanceof ApiProblemError) {
    if (error.status === 422 || error.status === 400) return 'validation';
    if (error.status === 403) return 'denied';
    if (error.status === 404) return 'missingProject';
    if (error.status === 401) return 'session';
  }
  return 'retryable';
}

/** Failures a second identical attempt cannot fix. */
export function isRetryableFailure(failure: CreateFailure): boolean {
  return failure === 'retryable';
}

export function stepIndex(step: GuidedStep): number {
  return GUIDED_STEPS.indexOf(step);
}

/** FlexUI fields reject `helperText: undefined`; spread this instead. */
export const withHelper = (text: string) => (text ? { helperText: text } : {});

export function isBriefDirty(values: BriefValues, fields?: readonly BriefField[]): boolean {
  return (fields ?? (['name', 'topic', 'description', 'slideCount'] as const))
    .map((f) => values[f])
    .some((value) => value.trim() !== '');
}

export type ServerFieldIssues = Partial<Record<'name' | 'description', BriefIssue>>;

/**
 * Reads the Problem Details `errors[]` extension (docs/engineering/06: `path`, `code`, `message`).
 * Only the two fields Create accepts are mapped, and the localized copy is ours: the server
 * `message` is never rendered, and `code` is only used to pick between two known issues.
 */
export function extractFieldIssues(error: unknown): ServerFieldIssues {
  if (!(error instanceof ApiProblemError)) return {};
  const problem = error.problem as { errors?: unknown } | null;
  const entries = Array.isArray(problem?.errors) ? (problem.errors as unknown[]) : [];
  const issues: ServerFieldIssues = {};
  for (const entry of entries) {
    const { code, path } = (entry ?? {}) as { code?: unknown; path?: unknown };
    if (path !== 'name' && path !== 'description') continue;
    issues[path] =
      typeof code === 'string' && /too_(long|big)/u.test(code) ? 'tooLong' : 'rejected';
  }
  return issues;
}
